import { Vue, prop, Options } from 'vue-class-component';
import VueSlider from 'vue-slider-component';
import { WidgetWrapper } from '@/components/widget-wrapper';
import '@/styles/slider.scss';
import { Inject, Watch } from 'vue-property-decorator';
import type { VueI18n } from 'vue-i18n';
import { WriteupModuleContext } from '@/store/writeup';
import { getCreditScoreGoodness } from '@/common/helpers';
import { Routes } from '@/router/routes';
import { Button, ButtonTypes } from '@/components/button';
import firstUp from 'lodash/upperFirst';
import clsx from 'clsx';
import { ServiceContainer } from '@/services';
import { AMPLITUDE_LABELS } from '@/common';
import styles from './styles.module.scss';

class CreditScoreSliderProps {
	loading = prop<boolean>({ default: true })

	value = prop<number>({ default: 688 })

	min = prop<number>({ default: 300 })

	max = prop<number>({ default: 850 })

	step = prop<number>({ default: 1 })

	goodness = prop<string>({ default: 'fair' })

	onChange = prop<Function>({ default: () => () => undefined })

	isScoreUpdated = prop<boolean>({ default: false })

	onScoreReset = prop<Function>({ default: undefined })

	showCreditAppLauncher = prop<Function>({ default: () => () => undefined })

	isCbcEnabled = prop<number>({ default: 0 })

	lenderSocketEnabled = prop<boolean>({ default: false })
}

@Options({
	components: {
		VueSlider,
	},
})
export class CreditScoreSlider extends Vue.with(CreditScoreSliderProps) {
	@Inject() readonly i18n!: VueI18n;

	@Inject() readonly services!: ServiceContainer;

	@Inject() readonly writeupModule!: WriteupModuleContext;

	sliderValue = this.value;

	get scoreGoodness(): string {
		return getCreditScoreGoodness(this.sliderValue);
	}

	@Watch('value')
	updateValue(value: number): void {
		this.sliderValue = value;
	}

	@Watch('sliderValue')
	handleSliderValueChange(value: number) {
		this.onChange(value);
	}

	triggerSoftPullEvent(action: string) {
		const eventData = {
			Label: AMPLITUDE_LABELS.customizeDeal,
			Action: action,
			...this.$route.meta,
		};

		this.services.amplitude.sendEvent(eventData);
	}

	triggerEvent() {
		const eventData = {
			Label: 'CustomizeDeal',
			Action: 'LearnMore',
			...this.$route.meta,
		};

		this.services.amplitude.sendEvent(eventData);
	}

	canShowMore() {
		return this.isCbcEnabled || this.lenderSocketEnabled;
	}

	render() {
		return (
			<WidgetWrapper active={ this.sliderValue !== 0 }
				class={ styles.creditScoreSlider }
				loading={ this.loading }
				title={ this.i18n.t('creditScoreSlider.title') } value={
					this.isScoreUpdated
						? firstUp(`${this.i18n.t(`creditScoreGoodness.${this.scoreGoodness}`)}`)
						: `${this.sliderValue} (${this.i18n.t(`creditScoreGoodness.${this.scoreGoodness}`)})`
				}
				editIcon={true}
				unFormattedValue={this.sliderValue}
				onChange={this.updateValue}
				min={this.min}
				max={this.max}
				isScoreUpdated={this.isScoreUpdated}
			>
				<vue-slider
					v-show={ !this.isScoreUpdated }
					class={ styles.creditScoreSliderSlider }
					v-model={ this.sliderValue } min={ this.min }
					max={ this.max } interval={ this.step }
					dotSize={ 24 } tooltip={ 'none' } silent={ true }
				/>
				<div v-show={ this.isCbcEnabled && !this.isScoreUpdated } class={ styles.creditScoreSliderSubtitle }>
					{ this.i18n.t('creditScoreSlider.subtitle') }
				</div>
				{
					this.isScoreUpdated
						? <div class={ styles.creditScoreSliderDescription }>
							<Button
								class={ styles.creditScoreSliderCenterButton }
								type={ ButtonTypes.link }
								onClick={ this.onScoreReset }
								inline={ true }>
								{ this.i18n.t('creditScoreWidget.subtitle') }
							</Button>
						</div>
						: <div class={ styles.creditScoreSliderDescription } v-show={ this.isCbcEnabled }>
							{ this.i18n.t('creditScoreSlider.description') }
						</div>
				}
				{
					this.isScoreUpdated
						? <div class={
							clsx([styles.creditScoreSliderDescription, styles.creditScoreSliderLink])
						}>
							{ this.i18n.t('creditScoreWidget.description') }
							<Button
								class={ styles.creditScoreSliderCenterButton }
								type={ ButtonTypes.link }
								to={ Routes.CREDIT_SCORE.path }
								onClick={ () => { this.triggerSoftPullEvent('SoftPull'); } }
								inline={ true }>
								{ this.i18n.t('creditScoreWidget.differentPerson') }
							</Button>
						</div>
						: <div class={ styles.creditScoreSliderDescription }>
							<Button
								v-show={ this.canShowMore() }
								class={ styles.creditScoreSliderCenterButton }
								type={ ButtonTypes.link } onClick={ () => { this.triggerEvent(); this.showCreditAppLauncher(); } }>
								{this.i18n.t('creditScoreSlider.learnMore')}</Button>
						</div>
				}
			</WidgetWrapper>
		);
	}
}
